import { ChangeEvent, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '@/shared/components/TextField/TextField'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import { DEFAULT_MAX_GUESTS } from '../../config'
import { PageElement } from '../../schema/elements'
import { RichTextDoc } from '../../schema/richText'
import { Localized } from '../../schema/localized'
import { resolveEditorAssetUrl } from '../../utils/assets'
import PhotoField from '../PhotoField/PhotoField'
import RichTextEditor from '../RichTextEditor/RichTextEditor'
import TokenSelect from './TokenSelect'
import RoomPicker from '../RoomPicker/RoomPicker'
import styles from './PropertiesPanel.module.scss'

interface ElementPropertiesProps {
  element: PageElement
  language: string
  onChange: (patch: Record<string, unknown>) => void
}

type FieldEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>

const readLocalized = (
  field: Localized<string> | undefined,
  language: string
) => field?.[language] ?? ''

const writeLocalized = (
  field: Localized<string> | undefined,
  language: string,
  value: string
) => ({ ...(field ?? {}), [language]: value })

/**
 * Where the guest's eye should land when a photo is cropped.
 *
 * Clicking the picture rather than typing two numbers: the owner is not choosing
 * coordinates, they are pointing at the dish.
 */
function FocalPointPicker({
  assetId,
  point,
  onChange
}: {
  assetId: string
  point: { x: number; y: number }
  onChange: (point: { x: number; y: number }) => void
}) {
  const { t } = useTranslation()

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    onChange({
      x: Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)),
      y: Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height))
    })
  }

  return (
    <div className={styles.field}>
      <p className={`label-bold-medium ${styles.fieldLabel}`}>
        {t('WEBSITE.PROPS.FOCAL_POINT')}
      </p>
      <button
        type="button"
        className={styles.focalArea}
        aria-label={t('WEBSITE.PROPS.FOCAL_POINT')}
        onClick={handleClick}
      >
        <img
          src={resolveEditorAssetUrl(assetId)}
          alt=""
          className={styles.focalImage}
        />
        <span
          className={styles.focalDot}
          // Computed geometry, the one case an inline style is for.
          style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
        />
      </button>
      <p className={`body-small ${styles.hint}`}>
        {t('WEBSITE.PROPS.FOCAL_HINT')}
      </p>
    </div>
  )
}

/**
 * What a booking form is made of, as the owner sets it: the rooms a table may
 * be picked in, the words on the button, the consent line, the most people
 * one booking may be for, and a telephone for the guest who would rather
 * ring. The fields the guest fills in are the form's own and not settable;
 * a booking needs the same things at every venue.
 */
function ReservationProperties({
  element,
  language,
  onChange
}: {
  element: Extract<PageElement, { type: 'reservation' }>
  language: string
  onChange: (patch: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()

  return (
    <>
      <RoomPicker
        label={t('WEBSITE.PROPS.ROOM')}
        value={element.rooms ?? []}
        onChange={rooms => onChange({ rooms })}
      />
      <p className={`body-small ${styles.hint}`}>
        {t('WEBSITE.PROPS.ROOM_HINT')}
      </p>
      <TextField
        label={t('WEBSITE.PROPS.RESERVATION_LABEL')}
        focused
        value={readLocalized(element.label, language)}
        onChange={(event: FieldEvent) =>
          onChange({
            label: writeLocalized(element.label, language, event.target.value)
          })
        }
      />
      <TextField
        label={t('WEBSITE.PROPS.RESERVATION_GUESTS')}
        type="number"
        focused
        value={String(element.maxGuests ?? DEFAULT_MAX_GUESTS)}
        onChange={(event: FieldEvent) => {
          const guests = Number.parseInt(event.target.value, 10)
          // The schema allows 1..99; anything else keeps the default.
          onChange({
            maxGuests:
              Number.isFinite(guests) && guests >= 1
                ? Math.min(guests, 99)
                : DEFAULT_MAX_GUESTS
          })
        }}
      />
      <TextField
        label={t('WEBSITE.PROPS.RESERVATION_CONSENT')}
        multiline
        lines={3}
        focused
        value={readLocalized(element.consent, language)}
        onChange={(event: FieldEvent) =>
          onChange({
            consent: writeLocalized(
              element.consent,
              language,
              event.target.value
            )
          })
        }
      />
      <TextField
        label={t('WEBSITE.PROPS.RESERVATION_PHONE')}
        focused
        placeholder="+48"
        value={element.phone ?? ''}
        onChange={(event: FieldEvent) =>
          onChange({ phone: event.target.value })
        }
      />
      <p className={`body-small ${styles.hint}`}>
        {t('WEBSITE.PROPS.RESERVATION_HINT')}
      </p>
    </>
  )
}

function ElementProperties(props: ElementPropertiesProps) {
  const { element, language, onChange } = props
  const { t } = useTranslation()

  const alignSelect = (
    <TokenSelect
      label={t('WEBSITE.PROPS.ALIGN')}
      value={'align' in element ? element.align : 'left'}
      options={['left', 'center', 'right']}
      translate={value => t(`WEBSITE.ALIGN.${value}`)}
      onChange={value => onChange({ align: value })}
    />
  )

  switch (element.type) {
    case 'heading':
      return (
        <>
          <TextField
            label={t('WEBSITE.PROPS.HEADING_TEXT')}
            focused
            value={readLocalized(element.text, language)}
            onChange={(event: FieldEvent) =>
              onChange({
                text: writeLocalized(element.text, language, event.target.value)
              })
            }
          />
          <TokenSelect
            label={t('WEBSITE.PROPS.HEADING_LEVEL')}
            value={String(element.level)}
            options={['2', '3']}
            translate={value => t(`WEBSITE.HEADING_LEVEL.${value}`)}
            onChange={value => onChange({ level: Number(value) })}
          />
          {alignSelect}
        </>
      )

    case 'text': {
      const doc = (element.content as Localized<RichTextDoc>)[language]
      return (
        <>
          <RichTextEditor
            label={t('WEBSITE.PROPS.TEXT')}
            value={doc}
            onChange={next =>
              onChange({ content: { ...element.content, [language]: next } })
            }
          />
          {alignSelect}
        </>
      )
    }

    case 'image':
      return (
        <>
          {/* The same tile and the same crop dialog as every other photo in
              this app. */}
          <PhotoField
            label={t('WEBSITE.PROPS.PHOTO')}
            slotId={`website-image-${element.id}`}
            value={element.assetId}
            ratio={element.ratio}
            onChange={assetId => onChange({ assetId })}
          />
          <TextField
            label={t('WEBSITE.PROPS.ALT')}
            focused
            value={readLocalized(element.alt, language)}
            onChange={(event: FieldEvent) =>
              onChange({
                alt: writeLocalized(element.alt, language, event.target.value)
              })
            }
          />
          <p className={`body-small ${styles.hint}`}>
            {t('WEBSITE.PROPS.ALT_HINT')}
          </p>
          <TokenSelect
            label={t('WEBSITE.PROPS.RATIO')}
            value={element.ratio}
            options={['1:1', '4:3', '16:9', '3:4', 'original']}
            /*
              The colon is i18next's namespace separator, so "16:9" would be
              read as namespace "16", key "9". Ratios are keyed with underscores.
            */
            translate={value => t(`WEBSITE.RATIO.${value.replace(':', '_')}`)}
            onChange={value => onChange({ ratio: value })}
          />
          <FocalPointPicker
            assetId={element.assetId}
            point={element.focalPoint}
            onChange={point => onChange({ focalPoint: point })}
          />
          <CheckBox
            label={t('WEBSITE.PROPS.ROUNDED')}
            checked={element.rounded}
            value={element.rounded}
            onChange={value => onChange({ rounded: value })}
          />
        </>
      )

    case 'button':
      return (
        <>
          <TextField
            label={t('WEBSITE.PROPS.BUTTON_LABEL')}
            focused
            value={readLocalized(element.label, language)}
            onChange={(event: FieldEvent) =>
              onChange({
                label: writeLocalized(
                  element.label,
                  language,
                  event.target.value
                )
              })
            }
          />
          <TextField
            label={t('WEBSITE.PROPS.BUTTON_HREF')}
            focused
            placeholder="https://"
            value={element.target.kind === 'url' ? element.target.href : ''}
            onChange={(event: FieldEvent) =>
              onChange({ target: { kind: 'url', href: event.target.value } })
            }
          />
          <TokenSelect
            label={t('WEBSITE.PROPS.BUTTON_VARIANT')}
            value={element.variant}
            options={['primary', 'secondary', 'outline']}
            translate={value => t(`WEBSITE.BUTTON_VARIANT.${value}`)}
            onChange={value => onChange({ variant: value })}
          />
          {alignSelect}
        </>
      )

    case 'reservation':
      return (
        <ReservationProperties
          element={element}
          language={language}
          onChange={onChange}
        />
      )

    case 'spacer':
      return (
        <TokenSelect
          label={t('WEBSITE.PROPS.SIZE')}
          value={element.size}
          options={['S', 'M', 'L', 'XL']}
          translate={value => t(`WEBSITE.SIZE.${value}`)}
          onChange={value => onChange({ size: value })}
        />
      )

    default:
      /*
        Galleries, videos, maps and the three blocks whose widgets arrive in
        later phases have no settings worth showing yet. Saying so beats an empty
        panel, which reads as a bug.
      */
      return (
        <p className={`body-medium ${styles.hint}`}>
          {t('WEBSITE.PROPS.NO_SETTINGS')}
        </p>
      )
  }
}

export default ElementProperties
